/* eslint-disable react-hooks/exhaustive-deps */
import React, { use, useCallback, useContext, useEffect, useRef, useState } from 'react';
import Input from '#lib/components/input.jsx';
import { DocumentsModal } from '#components/documents-modal.jsx';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { mergeClasses, cn, addClasses } from '#root/lib/helpers/class.js';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/solid';
import { isFunction } from '#lib/utils/function.js';
import select from '#lib/components/select.jsx';
import Checkbox from '#lib/components/checkbox.jsx';
import { ItineraryContext } from '#routes/manage/itinerary/edit.jsx';
import Button from '#lib/components/button.jsx';
import FloatingTooltip from '#components/tooltip.jsx';
import AiDescription from '#components/ai-description.jsx';
import { Icon } from '@iconify-icon/react';
import { defaultAccessValue, defaultDisplayValue, defaultMakeReactKey, formatData } from '#helpers/helpers.js';
import { useGallery } from '#@itinerary/gallery/use-gallery.js';
import { GalleryEditor } from '#@itinerary/gallery/gallery-editor.jsx';
import { AddRowButton } from './row.jsx';
import { PlacesDropdown } from '#components/places-dropdown.jsx';
import HTMLEditor from '#components/html-editor.jsx';
import { sameIdOrImportId } from '#@itinerary/places/helpers.js';
import { PLAN_TYPES } from './types.js';
import { FlightModal } from '#components/flight-modal.jsx';
import toast from 'react-hot-toast';
import { AddressModal } from '#components/address-modal.jsx';
import useEffectAfterMount from '#hooks/global/use-effect-after-mount.js';
import useAuth from '#lib/hooks/use-auth.jsx';
import Dialog from '#lib/components/dialog.jsx';
import { useHotelbedsSearch } from './use-hotelbedsSearch.js';
import ImagePreview from '#components/images/image-preview.jsx';
import { capitalize } from '#lib/utils/string.js';
import { UserContext } from '#src/index.jsx';

export function MainDetail(props) {
    const context = useContext(ItineraryContext);
    const { intlLang: language, themeId, currency } = context ?? {};

    const {
        type,
        name,
        description,
        gallery,
        price,
        roomGallery,
        highlight,
        onEdit,
        onMoveGallery,
        dayId,
        planId,
        roomId,
        onChange: providedOnChange,
        city,
        invalidFields,
        handleError,
        setUnsavedChanges,
        currencies,
        isTemplate,
        templateError,
        isOpen: isRoomOpen,
        rooms
    } = props;

    const titleRef = useRef();
    const priceRef = useRef();
    const [isOpenAiDescription, seIsOpenAiDescription] = useState(false);

    useEffect(() => {
        const title = titleRef.current;
        if (title && !isTemplate) title.focus();
    }, [titleRef.current, isRoomOpen]);

    const onChange = (event) => {
        const { name } = event.target;
        const formattedValue = formatData(event);
        // se sto cambiando il prezzo di una stanza, aggiorna il prezzo complessivo del plan
        if (roomId && name === 'price') {
            const filteredRooms = rooms.filter((room) => room.id !== roomId);
            let totalRoomPrice = filteredRooms.reduce((acc, curr) => {
                acc += Number(curr.price);
                return acc;
            }, 0);
            totalRoomPrice += Number(formattedValue);
            onEdit([dayId, planId], name, totalRoomPrice);
        }

        if (invalidFields?.includes(name)) handleError({ dayId, planId, field: name });
        if (isFunction(providedOnChange)) return providedOnChange(event);
        onEdit([dayId, planId, roomId], name, formattedValue);
    };

    const handleFocusOnPrice = () => {
        if (priceRef.current == document.activeElement && roomId)
            toast(_t.edit.planner.roomPriceWarning(), {
                icon: '⚠️'
            });
    };

    const handleCheckboxValue = useCallback(
        (name, value) => {
            onEdit([dayId, planId], name, value);
        },
        [dayId, onEdit, planId]
    );

    const galleryProps = useGallery({
        initialData: roomGallery ?? gallery,
        onAddImage: (state) => {
            if (isFunction(providedOnChange)) providedOnChange({ target: { name: 'gallery', value: state } });
            if (isFunction(onEdit)) onEdit([dayId, planId, roomId], 'gallery', state);
        },
        onEdit: (state) => {
            if (isFunction(providedOnChange)) providedOnChange({ target: { name: 'gallery', value: state } });
            if (isFunction(onEdit)) onEdit([dayId, planId, roomId], 'gallery', state);
        },
        onUpload: (state) => {
            if (isFunction(providedOnChange)) providedOnChange({ target: { name: 'gallery', value: state } });
            if (isFunction(onEdit)) onEdit([dayId, planId, roomId], 'gallery', state);
        },
        onDelete: (state) => {
            if (isFunction(providedOnChange)) providedOnChange({ target: { name: 'gallery', value: state } });
            if (isFunction(onEdit)) onEdit([dayId, planId, roomId], 'gallery', state);
        },
        setUnsavedChanges
    });

    const [exchangeRate, setExchangeRate] = useState(
        currencies?.find((c) => c.name === currency || c.id === currency)?.exchangeRate
    );

    // update total price when currency changes
    useEffectAfterMount(() => {
        if (!price || !exchangeRate) return;
        const currentExchangeRate = currencies?.find((c) => c.name === currency || c.id === currency)?.exchangeRate;
        const newPrice = (price * currentExchangeRate) / exchangeRate;
        onEdit([dayId, planId, roomId], 'price', Number(newPrice.toFixed(2)));
        setExchangeRate(currentExchangeRate);
    }, [currency]);

    const onAddHotel = useCallback(
        (hotel) => {
            if (isTemplate) {
                for (const [key, value] of Object.entries(hotel)) {
                    onChange({ target: { name: key, value } });
                }
                return;
            }
            onEdit([dayId, planId], hotel, null, true);
        },
        [dayId, onEdit, planId]
    );
    const [isHotelModalOpen, setIsHotelModalOpen] = useState(false);

    return (
        <div className="flex flex-col gap-2">
            <GalleryEditor
                {...galleryProps}
                onMoveGallery={onMoveGallery}
                dayId={dayId}
                planId={planId}
                roomId={roomId}
                template={isTemplate}
            />
            <div className="flex w-full gap-2">
                <div className="flex items-end w-full gap-2">
                    <Input.Full
                        ref={titleRef}
                        label={_t.edit.fields.name()}
                        placeholder={_t.edit.fields.name()}
                        value={name || ''}
                        name="name"
                        onChange={onChange}
                        hasError={invalidFields?.includes('name') || templateError?.name}
                        className="w-full"
                    />
                    {type === 'accommodation' && (
                        <FloatingTooltip placement="top" content={_t.edit.planner.hotelSearchTooltip()}>
                            <div
                                className="flex items-center justify-center rounded-md border border-neutral-300 bg-white shadow-sm cursor-pointer w-[37px] py-2 text-neutral-700 hover:text-primary-600"
                                onClick={() => setIsHotelModalOpen(true)}
                            >
                                <Icon icon="carbon:hotel" width="1.2rem" height="1.2rem" />
                            </div>
                        </FloatingTooltip>
                    )}
                </div>

                <Input.Full
                    label={_t.edit.fields.price(!!currency, currency)}
                    ref={priceRef}
                    name="price"
                    type="number"
                    min="0"
                    step="any"
                    pattern="\d+(\.\d{2})?"
                    value={price || 0}
                    onFocus={handleFocusOnPrice}
                    onChange={onChange}
                    onBlur={() => onChange({ target: { name: 'price', value: Number(price.toFixed(2)) } })}
                    hasError={invalidFields?.includes('price')}
                    className="w-[160px]"
                />
            </div>
            {type === 'info' && <InfoDetail {...props} onChange={onChange} />}
            <div>
                <AiDescription
                    contextName="plan"
                    isOpen={isOpenAiDescription}
                    setIsOpen={seIsOpenAiDescription}
                    setAiDescription={(value) => {
                        if (isFunction(providedOnChange)) providedOnChange({ target: { name: 'description', value } });
                        if (isFunction(onEdit)) onEdit([dayId, planId, roomId], 'description', value);
                    }}
                    name={name}
                    city={city}
                    language={language}
                    themeId={themeId}
                />
                <HTMLEditor
                    key={planId}
                    onEditorChange={(_, editor) => {
                        onChange({ target: { name: 'description', value: editor.getContent() } });
                    }}
                    value={description ?? ''}
                />
            </div>

            {type === 'custom' && (
                <div className="flex gap-x-2">
                    <Checkbox.Full
                        onChange={() => handleCheckboxValue('highlight', !highlight)}
                        checked={highlight}
                        label={_t.edit.fields.highlight()}
                    />
                </div>
            )}
            <HotelModal
                isOpen={isHotelModalOpen}
                onClose={() => setIsHotelModalOpen(false)}
                language={language}
                fillPlan={onAddHotel}
            />
        </div>
    );
}

export function InfoDetail(props) {
    const {
        name,
        infoType,
        previewMode,
        dayId,
        planId,
        places,
        editPlaces,
        infoId,
        filters,
        defaultText,
        invalidFields,
        handleError,
        totalFilters,
        setUnsavedChanges,
        ...rest
    } = props;

    const usablePlaces = structuredClone(places)
        .map((v) => v.info)
        .flatMap((v) => v);

    const onChangeInfo = (value) => {
        const current = usablePlaces.find((place) => place.id === infoId);
        const place = usablePlaces.find((place) => place.name === value);
        editPlaces({
            planData: {
                dayId,
                planId,
                field: {
                    infoId: place.id,
                    name: place.name,
                    description: place?.description
                }
            },
            resultData: {
                id: infoId,
                type: 'info',
                name: current?.place,
                field: 'locked',
                data: { locked: false }
            },
            multiAction: true
        });
        if (invalidFields?.includes('info')) handleError({ dayId, planId, field: 'info' });
        if (invalidFields?.includes('defaultText')) handleError({ dayId, planId, field: 'defaultText' });
    };

    return (
        <div className="flex flex-col gap-2 pb-1">
            <PlacesDropdown
                options={usablePlaces}
                hasError={invalidFields?.includes('info')}
                onChange={onChangeInfo}
                makeReactKey={defaultMakeReactKey}
                accessValue={defaultAccessValue}
                displayValue={defaultDisplayValue}
                value={usablePlaces.find((place) => sameIdOrImportId(place, infoId))?.name}
                className={addClasses('cursor-pointer')}
                buttonClassName={addClasses('text-ellipsis whitespace-nowrap truncate')}
                placeholder={_t.edit.planner.selectPlace()}
                label={_t.edit.fields.place()}
                name="places"
            />
        </div>
    );
}

export function SuggestionDetail(props) {
    const {
        name,
        suggestionType,
        previewMode,
        dayId,
        planId,
        places,
        editPlaces,
        suggestionId,
        filters,
        defaultText,
        invalidFields,
        handleError,
        totalFilters,
        setUnsavedChanges,
        ...rest
    } = props;

    const usablePlaces = structuredClone(places)
        .map((v) => v.suggestions)
        .flatMap((v) => v);
    const [mode, setMode] = useState(previewMode || 'embed');
    const suggestionTypesOptions = totalFilters.filter((el) => usablePlaces.find((x) => x.filterId == el.id));

    const onChange = useCallback(
        (place) => {
            editPlaces({ planData: { dayId, planId, field: 'suggestionType', value: place.name } });
            if (invalidFields?.includes(name)) handleError({ dayId, planId, field: name });
        },
        [dayId, editPlaces, planId, usablePlaces]
    );

    const onClickRadio = useCallback(
        (event) => {
            const { value } = event.target;
            setMode(value);
            const suggestion = usablePlaces.find((place) => place.id === suggestionId);
            editPlaces({
                planData: {
                    dayId,
                    planId,
                    field: { previewMode: value, defaultText: _t.edit.planner.defaultTexts[suggestionType][value]() }
                },
                multiAction: true
            });
            if (value === 'modal')
                editPlaces({
                    planData: {
                        dayId,
                        planId,
                        field: {
                            name: 'Suggestion'
                        }
                    },
                    multiAction: true
                });
            else editPlaces({ planData: { dayId, planId, field: { name: suggestion.name } }, multiAction: true });
            if (invalidFields?.includes('suggestion')) handleError({ dayId, planId, field: 'suggestion' });
        },
        [dayId, editPlaces, planId, setMode]
    );

    const onChangeText = useCallback(
        (event) => {
            const { name, value } = event.target;
            editPlaces({ planData: { dayId, planId, field: 'defaultText', value: value } });
            if (invalidFields?.includes(name)) handleError({ dayId, planId, field: name });
        },
        [dayId, editPlaces, planId]
    );

    const onChangeSuggestion = (value) => {
        // const current = usablePlaces.find((place) => place.id === suggestionId);
        const place = usablePlaces.find((place) => place.name === value);
        editPlaces({
            planData: {
                dayId,
                planId,
                field: {
                    suggestionType: place?.filterName,
                    suggestionId: place.id,
                    defaultText: _t.edit.planner.defaultTexts[place?.filterName][mode](),
                    name: place.name
                }
                // resultData: {
                //     id: suggestionId,
                //     type: 'suggestion',
                //     name: current?.place,
                //     field: 'locked',
                //     data: { locked: false }
                // }
            },
            multiAction: true
        });
        if (invalidFields?.includes('suggestion')) handleError({ dayId, planId, field: 'suggestion' });
        if (invalidFields?.includes('defaultText')) handleError({ dayId, planId, field: 'defaultText' });
    };

    return (
        <div className="flex flex-col gap-2 pb-1">
            <div className="flex gap-x-2">
                <span className="text-sm font-bold">{_t.edit.planner.previewMode()}</span>
                <FloatingTooltip content="Change the view of this plan when published" placement="top">
                    <QuestionMarkCircleIcon className="w-5 h-5 cursor-pointer text-neutral-300 hover:text-neutral-500" />
                </FloatingTooltip>
            </div>

            <form className="flex gap-x-4">
                <div className="flex items-center gap-x-2">
                    <label htmlFor="radio-0">{_t.edit.planner.modal()}</label>
                    <input
                        id="radio-0"
                        type="radio"
                        name="opzione"
                        value="modal"
                        checked={mode === 'modal'}
                        onChange={onClickRadio}
                    />
                </div>
                <div className="flex items-center gap-x-2">
                    <label htmlFor="radio-1">{_t.edit.planner.embed()}</label>
                    <input
                        id="radio-1"
                        type="radio"
                        name="opzione"
                        value="embed"
                        checked={mode === 'embed'}
                        onChange={onClickRadio}
                    />
                </div>
            </form>
            {mode === 'embed' && (
                <PlacesDropdown
                    options={usablePlaces}
                    hasError={invalidFields?.includes('suggestion')}
                    onChange={onChangeSuggestion}
                    makeReactKey={defaultMakeReactKey}
                    accessValue={defaultAccessValue}
                    displayValue={defaultDisplayValue}
                    value={usablePlaces.find((place) => sameIdOrImportId(place, suggestionId))?.name}
                    className={addClasses('cursor-pointer')}
                    buttonClassName={addClasses('text-ellipsis whitespace-nowrap truncate')}
                    placeholder={_t.edit.planner.selectPlace()}
                    label={_t.edit.fields.place()}
                    name="places"
                />
            )}
            <select.Full
                options={suggestionTypesOptions}
                onChange={onChange}
                makeReactKey={defaultMakeReactKey}
                displayValue={(v) => _t.edit.filters[v.filterKey]()}
                label={_t.edit.fields.category()}
                key={suggestionType}
                defaultValue={suggestionTypesOptions.find((el) => el.name === suggestionType)}
                disabled={mode === 'embed'}
                className={addClasses('cursor-pointer')}
            />

            <div className="flex gap-x-2 items-center w-[100%]">
                <Input.Full
                    name="defaultText"
                    hasError={invalidFields?.includes('defaultText')}
                    onChange={onChangeText}
                    label={_t.edit.suggestionDefaultText(mode)}
                    value={defaultText}
                    className="w-[100%]"
                ></Input.Full>
            </div>
        </div>
    );
}

export function TransferDetail(props) {
    const {
        dayId,
        planId,
        onEdit,
        transferTypes,
        transportation,
        vehicleNumber,
        departureTime,
        arrivalTime,
        arrival,
        departure,
        phoneNumber,
        company,
        pax,
        meetingPoint,
        handleError,
        invalidFields,
        onChange: providedOnChange,
        handleShowMapModal
    } = props;

    const transferTypeOptions = transferTypes
        ? Object.entries(transferTypes)
              .slice(0, -1)
              .map(([key, value]) => {
                  return {
                      name: key,
                      value: value
                  };
              })
        : [];

    const transferOption = transferTypeOptions.find((el) => el.name === transportation);
    const transferType = _t.dropdown.transferType?.[transportation ?? 'O']() || '';

    const onChange = useCallback(
        (event) => {
            const { name, value } = event.target;
            if (invalidFields?.includes(name)) handleError({ dayId, planId, field: name });
            if (isFunction(providedOnChange)) return providedOnChange(event);
            onEdit([dayId, planId], name, value);
        },
        [dayId, onEdit, planId, providedOnChange]
    );
    const onChangeSelect = useCallback(
        (value) => {
            if (isFunction(providedOnChange))
                providedOnChange({ target: { name: 'transportation', value: value.name } });
            if (isFunction(onEdit)) onEdit([dayId, planId], 'transportation', value.name);
        },
        [dayId, planId, onEdit]
    );

    return (
        <div className="flex flex-col gap-2 pb-1">
            <Input.Full
                label={_t.edit.fields.departure()}
                value={departure || ''}
                hasError={invalidFields?.includes('departure')}
                name="departure"
                onChange={onChange}
            />
            <Input.Full
                label={_t.edit.fields.arrival()}
                value={arrival || ''}
                name="arrival"
                onChange={onChange}
                hasError={invalidFields?.includes('arrival')}
            />
            <div className="flex gap-4">
                <div className="w-1/2">
                    <Input.Full
                        label={_t.edit.fields.departureTime()}
                        value={departureTime || ''}
                        name="departureTime"
                        type="time"
                        onChange={onChange}
                        hasError={invalidFields?.includes('departureTime')}
                    />
                </div>
                <div className="w-1/2">
                    <Input.Full
                        label={_t.edit.fields.arrivalTime()}
                        value={arrivalTime || ''}
                        name="arrivalTime"
                        type="time"
                        onChange={onChange}
                        hasError={invalidFields?.includes('arrivalTime')}
                    />
                </div>
            </div>
            {transportation != 'F' && (
                <select.Full
                    options={transferTypeOptions}
                    onChange={onChangeSelect}
                    makeReactKey={defaultMakeReactKey}
                    accessValue={(v) => v}
                    displayValue={(v) => _t.dropdown.transferType[v.name]()}
                    label={_t.edit.fields.transferType()}
                    value={transferOption || ''}
                    className={addClasses('cursor-pointer')}
                    hasError={invalidFields?.includes('transportation')}
                    inPortal={false}
                />
            )}
            <div className="flex flex-col">
                <label
                    className="flex items-baseline gap-2 mb-1 text-sm font-medium whitespace-nowrap text-neutral-700"
                    htmlFor="address"
                >
                    {_t.edit.fields.meetingPoint()}
                </label>
                <div className="relative flex w-full gap-2">
                    <Input.Full className="w-full" value={meetingPoint || ''} name="meetingPoint" onChange={onChange} />
                    <FloatingTooltip
                        content={_t.edit.openMapPanel()}
                        placement="top"
                        refClassName="absolute right-2 bottom-1 cursor-pointer text-neutral-700 hover:text-primary-600"
                    >
                        <Icon icon="eva:pin-outline" width="1.2rem" height="1.2rem" onClick={handleShowMapModal} />
                    </FloatingTooltip>
                </div>
            </div>
            <div className="flex gap-4">
                <Input.Full
                    label={_t.edit.fields.vehicleNumber(transportation && transportation !== 'O' ? transferType : '')}
                    value={vehicleNumber || ''}
                    name="vehicleNumber"
                    onChange={onChange}
                    className="w-1/2"
                    hasError={invalidFields?.includes('vehicleNumber')}
                />
                <Input.Full
                    label={_t.edit.fields.phoneNumber()}
                    value={phoneNumber || ''}
                    name="phoneNumber"
                    type="tel"
                    onChange={onChange}
                    className="w-1/2"
                />
            </div>
            <div className="flex gap-4">
                <Input.Full
                    label={_t.edit.fields.company()}
                    value={company || ''}
                    name="company"
                    className="w-2/3"
                    onChange={onChange}
                />
                <Input.Full
                    label={_t.edit.fields.passengers()}
                    value={pax || ''}
                    name="pax"
                    type="number"
                    min="1"
                    className="w-1/3"
                    onChange={onChange}
                    hasError={invalidFields?.includes('pax')}
                />
            </div>
        </div>
    );
}

export function ActivityDetail(props) {
    const {
        dayId,
        planId,
        onEdit,
        city,
        meetingPoint,
        startTime,
        duration,
        phoneNumber,
        invalidFields,
        handleError,
        onChange: providedOnChange,
        handleShowMapModal
    } = props;

    const onChange = useCallback(
        (event) => {
            const { name, value } = event.target;
            if (name === 'duration' && value % 1 !== 0) return;
            if (invalidFields?.includes(name)) handleError({ dayId, planId, field: name });
            if (isFunction(providedOnChange)) return providedOnChange(event);
            onEdit([dayId, planId], name, value);
        },
        [dayId, onEdit, planId, providedOnChange]
    );

    return (
        <div className="flex flex-col gap-2 pb-1">
            <div className="flex flex-col">
                <label
                    className="flex items-baseline gap-2 mb-1 text-sm font-medium whitespace-nowrap text-neutral-700"
                    htmlFor="address"
                >
                    {_t.edit.fields.meetingPoint()}
                </label>
                <div className="relative flex w-full gap-2">
                    <Input.Full className="w-full" value={meetingPoint || ''} name="meetingPoint" onChange={onChange} />
                    <FloatingTooltip
                        content={_t.edit.openMapPanel()}
                        placement="top"
                        refClassName="absolute right-2 bottom-1 cursor-pointer text-neutral-700 hover:text-primary-600"
                    >
                        <Icon icon="eva:pin-outline" width="1.2rem" height="1.2rem" onClick={handleShowMapModal} />
                    </FloatingTooltip>
                </div>
            </div>
            <div className="flex w-full gap-x-4">
                <Input.Full
                    hasError={invalidFields?.includes('city')}
                    label={_t.edit.fields.city()}
                    value={city || ''}
                    name="city"
                    onChange={onChange}
                    className="w-1/2"
                />
                <Input.Full
                    label={_t.edit.fields.phoneNumber()}
                    value={phoneNumber || ''}
                    name="phoneNumber"
                    onChange={onChange}
                    type="tel"
                    className="w-1/2"
                />
            </div>
            <div className="flex gap-4">
                <div className="w-1/2">
                    <Input.Full
                        label={_t.edit.fields.startTime()}
                        value={startTime || ''}
                        name="startTime"
                        type="time"
                        onChange={onChange}
                    />
                </div>
                <div className="w-1/2">
                    <Input.Full
                        label={_t.edit.fields.duration()}
                        value={duration || '0'}
                        name="duration"
                        type="number"
                        min="0"
                        step="5"
                        onChange={onChange}
                    />
                </div>
            </div>
        </div>
    );
}

export function AccommodationDetail(props) {
    const {
        dayId,
        planId,
        onEdit,
        category,
        stars,
        address,
        phoneNumber,
        city,
        checkInTime,
        checkOutTime,
        nights,
        invalidFields,
        handleError,
        onChange: providedOnChange,
        handleShowMapModal
    } = props;

    const cityRef = useRef();

    const onChange = useCallback(
        (event) => {
            const { name, value } = event.target;
            // validate stars number
            if (name === 'stars' && (value > 5 || value % 1 !== 0 || value < 0)) {
                return;
            }
            if (invalidFields?.includes(name)) handleError({ dayId, planId, field: name });
            if (isFunction(providedOnChange)) return providedOnChange(event);
            if (isFunction(onEdit)) onEdit([dayId, planId], name, value);
        },
        [dayId, onEdit, planId, providedOnChange]
    );

    return (
        <div className="flex flex-col gap-2">
            <div className="w-full">
                <div className="flex flex-col">
                    <label
                        className="flex items-baseline gap-2 mb-1 text-sm font-medium whitespace-nowrap text-neutral-700"
                        htmlFor="address"
                    >
                        {_t.edit.fields.address()}
                    </label>
                    <div className="relative flex w-full gap-2">
                        <Input.Full className="w-full" value={address || ''} name="address" onChange={onChange} />
                        <FloatingTooltip
                            content={_t.edit.openMapPanel()}
                            placement="top"
                            refClassName="absolute right-2 bottom-1 cursor-pointer text-neutral-700 hover:text-primary-600"
                        >
                            <Icon icon="eva:pin-outline" width="1.2rem" height="1.2rem" onClick={handleShowMapModal} />
                        </FloatingTooltip>
                    </div>
                </div>
            </div>
            <div className="flex w-full gap-x-4">
                <Input.Full
                    ref={cityRef}
                    hasError={invalidFields?.includes('city')}
                    label={_t.edit.fields.city()}
                    value={city || ''}
                    name="city"
                    onChange={onChange}
                    className="w-1/2"
                />
                <Input.Full
                    label={_t.edit.fields.phoneNumber()}
                    value={phoneNumber || ''}
                    name="phoneNumber"
                    onChange={onChange}
                    type="tel"
                    className="w-1/2"
                />
            </div>
            <div className="flex w-full gap-x-4">
                <Input.Full
                    label={_t.edit.fields.category()}
                    value={category || ''}
                    name="category"
                    onChange={onChange}
                    className="w-[50%]"
                    style={{ paddingRight: 0 }}
                />
                <Input.Full
                    label={_t.edit.fields.stars()}
                    value={stars || '0'}
                    name="stars"
                    type="number"
                    onChange={onChange}
                    className="w-[50%]"
                    style={{ paddingRight: 0 }}
                    min="0"
                    max="5"
                />
            </div>
            <div className="flex w-full gap-x-4">
                <Input.Full
                    label={_t.edit.fields.checkInTime()}
                    value={checkInTime || '1'}
                    name="checkInTime"
                    type="time"
                    onChange={onChange}
                    className="w-[50%]"
                    style={{ paddingRight: 0 }}
                    min="09:00"
                    max="20:00"
                />
                <Input.Full
                    label={_t.edit.fields.checkOutTime()}
                    value={checkOutTime || '1'}
                    name="checkOutTime"
                    type="time"
                    onChange={onChange}
                    className="w-[50%]"
                    style={{ paddingRight: 0 }}
                    min="09:00"
                    max="20:00"
                />
                <Input.Full
                    label={_t.edit.fields.nights()}
                    value={nights || '1'}
                    name="nights"
                    min="1"
                    type="number"
                    onChange={onChange}
                    className="w-[50%]"
                />
            </div>
            <Rooms {...props} />
        </div>
    );
}

export function Rooms(props) {
    const { id, rooms, onAdd, setUnsavedChanges } = props;

    return (
        <div className="flex flex-col gap-2 pb-1">
            <h2 className="mt-2 text-xl font-bold text-neutral-700">{_t.edit.planner.rooms()}</h2>

            {rooms && rooms.length > 0 && (
                <div className="flex flex-col">
                    {rooms.map((room, index) => {
                        return (
                            <RoomDetail
                                key={room.id}
                                roomId={room.id}
                                roomGallery={room.gallery}
                                roomName={room.name}
                                roomDescription={room.description}
                                roomPax={room.pax}
                                roomPrice={room.price}
                                roomIndex={index}
                                tempIndex={index + 1}
                                setUnsavedChanges={setUnsavedChanges}
                                rooms={rooms}
                                {...props}
                            />
                        );
                    })}
                </div>
            )}

            <AddRowButton onClick={() => onAdd(id)}>{_t.edit.planner.addRoom()}</AddRowButton>
        </div>
    );
}

export function RoomDetail(props) {
    const {
        selectedRoomId,
        setSelectedRoomId,
        tempIndex,
        roomId,
        roomIndex,
        roomGallery,
        roomName,
        roomDescription,
        roomPrice,
        roomPax,
        rooms,
        onDelete,
        onEdit,
        onMoveGallery,
        dayId,
        planId,
        name: planName,
        invalidFields,
        handleError,
        setUnsavedChanges,
        onChange: providedOnChange,
        showItineraryPreview,
        currency,
        currencies
    } = props;

    const isOpen = selectedRoomId === (roomId ?? roomIndex);

    const onChange = useCallback(
        (event) => {
            const { name, value } = event.target;
            if (name === 'pax' && value % 1 !== 0) return;
            if (invalidFields?.includes(name)) handleError({ dayId, planId, field: name });
            if (isFunction(providedOnChange)) return providedOnChange(event, roomId);
        },
        [dayId, planId, roomId, onEdit]
    );

    return (
        <div className="room">
            <div
                className={cn(
                    'py-2 pr-1 cursor-pointer rounded flex justify-between hover:bg-neutral-200 text-neutral-700 hover:text-neutral-800 hover:pl-2 transition-all',
                    showItineraryPreview ? '2xl:hover:bg-neutral-100' : 'xl:hover:bg-neutral-100'
                )}
                onClick={() => {
                    const id = roomId ?? roomIndex;
                    if (isOpen) {
                        setSelectedRoomId(null);
                    } else {
                        setSelectedRoomId(id);
                    }
                }}
            >
                <h3 className="font-bold text-md">{roomName || `Room ${tempIndex}`}</h3>
                <div className="flex gap-2">
                    <FloatingTooltip content={_t.global.delete()}>
                        <Icon
                            icon="ph:trash"
                            width="1.3rem"
                            height="1.3rem"
                            className="cursor-pointer"
                            onClick={() => onDelete(dayId, planId, roomId ?? roomIndex)}
                        />
                    </FloatingTooltip>
                    <FloatingTooltip
                        content={selectedRoomId === (roomId ?? roomIndex) ? _t.global.collapse() : _t.global.open()}
                    >
                        {selectedRoomId === (roomId ?? roomIndex) ? (
                            <MinusIcon className="w-6 h-6 pb-1" />
                        ) : (
                            <PlusIcon className="w-6 h-6 pb-1" />
                        )}
                    </FloatingTooltip>
                </div>
            </div>
            <div
                className={cn(
                    'flex flex-col space-y-2 p-0 m-0 pb-1 overflow-hidden',
                    isOpen ? 'block my-2 pl-[2px]' : 'hidden'
                )}
            >
                <MainDetail
                    key={roomId}
                    name={roomName}
                    city={planName}
                    description={roomDescription}
                    price={roomPrice}
                    onChange={onChange}
                    invalidFields={invalidFields}
                    handleError={handleError}
                    onEdit={onEdit}
                    dayId={dayId}
                    planId={planId}
                    roomId={roomId ?? roomIndex}
                    roomGallery={roomGallery}
                    onMoveGallery={onMoveGallery}
                    setUnsavedChanges={setUnsavedChanges}
                    isOpen={isOpen}
                    currency={currency}
                    currencies={currencies}
                    rooms={rooms}
                />
                <Input.Full
                    label={_t.edit.fields.pax()}
                    value={roomPax || ''}
                    name="pax"
                    type="number"
                    onChange={onChange}
                />
            </div>
        </div>
    );
}

export function PlanDetail(props) {
    const {
        name,
        title,
        startDate,
        type,
        onEdit,
        dayId,
        planId,
        dayIndex,
        handleCheckOut,
        nights,
        itineraryDocuments,
        invalidFields,
        handleError,
        documents,
        setShowTemplatesModal,
        templateError,
        onChange: providedOnChange,
        isTemplate,
        showItineraryPreview,
        transportation,
        handleAddFlight,
        profileLanguage,
        updateFlight,
        flightKey,
        needsUpdate,
        itineraryId,
        meetingPoint,
        address,
        city,
        phoneNumber,
        isBlocked
    } = props;

    const {
        meta: { data: userData }
    } = useAuth();

    const [dayID, setDayID] = useState(dayId);
    const [showFlightModal, setShowFlightModal] = useState(false);
    const [showMapModal, setShowMapModal] = useState(false);
    const [docsModal, setDocsModal] = useState(false);

    useEffect(() => {
        setDayID('day-' + dayIndex);
    }, [dayIndex]);

    const onChange = (event, roomId) => {
        const { name } = event.target;
        const formattedValue = formatData(event);
        if (isFunction(onEdit)) onEdit([dayID, planId, roomId], name, formattedValue);
        if (invalidFields?.includes(name)) handleError({ dayID, planId, field: name });
        if (isFunction(providedOnChange)) providedOnChange(event, roomId);
    };

    const showDocsModal = () => {
        setDocsModal((state) => !state);
    };

    const handleShowMapModal = () => setShowMapModal(true);

    return (
        <div
            className={cn(
                'relative flex flex-col gap-2 px-4 pb-4 pt-2 lg:border-l lg:border-l-neutral-200',
                !isTemplate
                    ? showItineraryPreview
                        ? '2xl:overflow-auto 2xl:max-h-[1000px]'
                        : 'xl:overflow-auto xl:max-h-[1000px]'
                    : 'h-full'
            )}
        >
            {!isTemplate ? (
                <DetailHeading>
                    <span className="overflow-hidden whitespace-nowrap text-ellipsis">{name || capitalize(type)}</span>

                    <div className="flex items-center gap-2">
                        {type === 'accommodation' && (
                            <FloatingTooltip content={_t.edit.planner.checkOut()} placement="top">
                                <Button onClick={() => handleCheckOut(name, nights)}>
                                    <Icon
                                        icon="material-symbols:travel-luggage-and-bags-outline-rounded"
                                        width="1rem"
                                        height="1rem"
                                    />
                                </Button>
                            </FloatingTooltip>
                        )}
                        {transportation === 'F' && flightKey && (
                            <FloatingTooltip content={_t.edit.planner.updateFlightTooltip()} placement="top">
                                <Button
                                    className={addClasses('relative')}
                                    onClick={() => updateFlight.mutate({ flightKey, itineraryId, dayIndex })}
                                    disabled={!needsUpdate}
                                >
                                    <Icon
                                        icon="f7:arrow-2-circlepath"
                                        width="1rem"
                                        height="1rem"
                                        className={cn(updateFlight.isLoading && 'animate-spin')}
                                    />
                                    {needsUpdate && (
                                        <div className="absolute -top-3 -right-5 w-[14px] h-[14px] rounded-[50%] bg-primary-500 border border-white"></div>
                                    )}
                                </Button>
                            </FloatingTooltip>
                        )}
                        <FloatingTooltip content={_t.edit.documentsSubText()} placement="top">
                            <Button
                                className={addClasses('relative')}
                                onClick={showDocsModal}
                                disabled={!itineraryDocuments?.length}
                            >
                                <Icon icon="mdi:file-document-outline" width="1rem" height="1rem" />
                                {documents && documents.length > 0 && (
                                    <div className="absolute -top-3 -right-5 w-[14px] h-[14px] rounded-[50%] bg-primary-500 border border-white"></div>
                                )}
                            </Button>
                        </FloatingTooltip>
                        {type !== 'suggestion' && (
                            <FloatingTooltip content={_t.edit.templatesTooltip()} placement="top">
                                <Button onClick={() => setShowTemplatesModal(true)}>
                                    <Icon icon="majesticons:book-plus-line" width="1rem" height="1rem" />
                                </Button>
                            </FloatingTooltip>
                        )}
                    </div>
                </DetailHeading>
            ) : (
                <div className="flex items-center w-full gap-4 ">
                    <div className="w-1/3">
                        <Input.Full
                            name="title"
                            hasError={templateError?.title}
                            onChange={providedOnChange}
                            label={_t.edit.fields.templateName()}
                            value={title}
                        />
                    </div>
                    <div className="w-1/3">
                        <select.Full
                            options={PLAN_TYPES}
                            onChange={(value) => onChange({ target: { name: 'type', value } })}
                            makeReactKey={defaultMakeReactKey}
                            displayValue={(v) => _t.edit.planner[v]()}
                            accessValue={(v) => v.id}
                            label={_t.edit.fields.type()}
                            value={type}
                            name={_t.edit.fields.type()}
                            className={addClasses('cursor-pointer')}
                            hasError={templateError?.type}
                            inPortal={false}
                        />
                    </div>
                    <div className="w-1/3 pt-6">
                        <Checkbox.Full
                            className="flex gap-2 w-fit"
                            label={_t.edit.planner.blockTemplate()}
                            name="isBlocked"
                            checked={isBlocked}
                            onChange={(e) => onChange({ target: { name: 'isBlocked', value: e.target.checked } })}
                            disabled={!userData.mainUser}
                        />
                    </div>
                </div>
            )}
            {type !== 'suggestion' && <MainDetail {...props} onChange={onChange} />}
            {type === 'suggestion' && <SuggestionDetail {...props} onChange={onChange} />}
            {type === 'accommodation' && (
                <AccommodationDetail {...props} onChange={onChange} handleShowMapModal={handleShowMapModal} />
            )}
            {type === 'activity' && (
                <ActivityDetail {...props} onChange={onChange} handleShowMapModal={handleShowMapModal} />
            )}
            {type === 'transfer' && (
                <TransferDetail {...props} onChange={onChange} handleShowMapModal={handleShowMapModal} />
            )}
            {docsModal && (
                <DocumentsModal
                    isOpen={docsModal}
                    onClose={showDocsModal}
                    onEdit={onEdit}
                    planDocuments={documents}
                    itineraryDocuments={itineraryDocuments}
                    dayId={dayID}
                    planId={planId}
                />
            )}
            {showMapModal && (
                <AddressModal
                    isOpen={showMapModal}
                    onClose={() => setShowMapModal(false)}
                    type={type}
                    defaultQuery={type === 'activity' ? meetingPoint : address || name}
                    defaultAddress={type === 'activity' ? meetingPoint : address}
                    defaultCity={city}
                    defaultPhone={phoneNumber}
                    accommodation={type === 'accommodation' && name}
                    onChange={onChange}
                />
            )}
            {showFlightModal && (
                <FlightModal
                    isOpen={showFlightModal}
                    onClose={() => setShowFlightModal(false)}
                    startDate={startDate}
                    currentDay={dayIndex}
                    addFlight={handleAddFlight}
                    profileLanguage={profileLanguage}
                />
            )}
        </div>
    );
}

export function DetailHeading(props) {
    const { children, className } = props;
    return (
        <h3
            className={mergeClasses(
                'w-full min-h-[30px] font-semibold text-neutral-700 flex justify-between items-center',
                className
            )}
        >
            {children}
        </h3>
    );
}

export function HotelModal(props) {
    const { isOpen, onClose, fillPlan } = props;
    const { profileLanguage: contextLanguage } = useContext(ItineraryContext) ?? {};
    const { getLanguage } = useContext(UserContext);
    const intlLanguage = getLanguage();
    const language = contextLanguage ?? intlLanguage;
    let lang = '';
    switch (language) {
        case 'en':
            lang = 'ENG';
            break;
        case 'es':
            lang = 'CAS';
            break;
        case 'it':
            lang = 'ITA';
            break;
        case 'ru':
            lang = 'RUS';
            break;
        default:
            lang = 'ENG';
            break;
    }
    useEffect(() => {
        switch (language) {
            case 'en':
                lang = 'ENG';
                break;
            case 'es':
                lang = 'CAS';
                break;
            case 'it':
                lang = 'ITA';
                break;
            case 'ru':
                lang = 'RUS';
                break;
            default:
                lang = 'ENG';
                break;
        }
        setSearchProps((prev) => ({ ...prev, language: lang }));
    }, [language]);
    const [hotels, setHotels] = useState([]);
    const [lek, setLek] = useState(null);
    const search = useHotelbedsSearch({
        onSuccess: (data) => {
            setHotels((prev) => [...prev, ...(data.results || [])]);
            setLek(data.LastEvaluatedKey || null);
        }
    });
    const [searchProps, setSearchProps] = useState({
        query: '',
        city: '',
        country: '',
        stars: 0,
        language: lang,
        lek
    });
    return (
        <Dialog isOpen={isOpen} onClose={onClose}>
            <Dialog.Panel className="flex flex-col w-2/3 h-[80%] overflow-y-auto px-4 py-2 space-y-4">
                <div className="pr-4">
                    <Dialog.CloseButton />
                    <b>{_t.edit.planner.hotelbedsSearchTitle()}</b>
                </div>
                <div className="grid w-full h-[30%] grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input.Full
                        label={_t.edit.fields.hotelName()}
                        placeholder={_t.edit.fields.hotelName()}
                        value={searchProps.query}
                        onChange={(e) => setSearchProps({ ...searchProps, query: e.target.value })}
                    />
                    <Input.Full
                        label={_t.edit.fields.hotelCity()}
                        placeholder={_t.edit.fields.hotelCity()}
                        value={searchProps.city}
                        onChange={(e) => setSearchProps({ ...searchProps, city: e.target.value })}
                    />
                    <Input.Full
                        label={_t.edit.fields.hotelCountry()}
                        placeholder={_t.edit.fields.hotelCountry()}
                        value={searchProps.country}
                        onChange={(e) => setSearchProps({ ...searchProps, country: e.target.value })}
                    />
                    <Input.Full
                        label={_t.edit.fields.stars()}
                        value={searchProps.stars}
                        onChange={(e) => setSearchProps({ ...searchProps, stars: e.target.value })}
                        type="number"
                        min="0"
                        max="5"
                    />
                    <Button
                        look="primary"
                        className={addClasses('col-span-2')}
                        onClick={() => {
                            setHotels([]);
                            setLek(null);
                            setSearchProps((prev) => ({ ...prev, lek: null }));
                            search.mutate(searchProps);
                        }}
                        busy={search.isLoading}
                    >
                        {_t.global.search()}
                    </Button>
                </div>
                <div className="flex flex-col h-[70%] flex-1 border-t overflow-y-auto ">
                    {hotels?.map((hotel) => (
                        <div
                            key={hotel.id}
                            className="flex gap-2 p-4 border-b cursor-pointer hover:bg-neutral-100 "
                            onClick={() => {
                                if (!confirm(_t.edit.planner.hotelbedsConfirm())) return;
                                const { id, address, city, country, images, ...data } = hotel;
                                data.address = [address, city, country].filter(Boolean).join(', ');
                                data.title = hotel.name;
                                data.city = city;
                                data.gallery = images.map((img) => ({
                                    id: img.split('/').at(-1),
                                    src: img + '?w=480&h=270&x=0&y=0&zoom=1',
                                    sourceType: 'G',
                                    coords: {
                                        x: 0,
                                        y: 0,
                                        imageWidth: 480,
                                        zoom: 1
                                    }
                                }));
                                fillPlan(data);
                                onClose();
                            }}
                        >
                            {hotel.images?.length > 0 && (
                                <div className="relative w-[200px] h-[150px] overflow-hidden rounded">
                                    <img
                                        src={hotel.images[0] + '?w=480&h=270&x=0&y=0&zoom=1'}
                                        alt={hotel.name}
                                        className="object-cover w-full h-full"
                                    />
                                </div>
                            )}
                            <div className="flex flex-col w-full h-full">
                                <div className="text-xl">{hotel.name}</div>
                                <div className="text-sm text-neutral-500">{hotel.address}</div>
                                <div className="text-sm text-neutral-500">
                                    {hotel.city}, {hotel.country}
                                </div>
                                <div className="flex items-end justify-end w-full h-full gap-1 mt-2">
                                    {[...Array(Number(hotel?.stars) || 0)].map((_, index) => (
                                        <Icon key={index} icon="mdi:star" className="text-yellow-500" />
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                    {lek && (
                        <span
                            className="w-full mx-auto mt-2 text-center underline cursor-pointer text-primary-500"
                            onClick={() => {
                                setSearchProps((prev) => ({ ...prev, lek }));
                                search.mutate({ ...searchProps, lek });
                            }}
                        >
                            {_t.components.table.loadMore()}
                        </span>
                    )}
                </div>
            </Dialog.Panel>
        </Dialog>
    );
}
